import { type CustomField, KIT_SYSTEM_FIELDS, KitStatusFilter } from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const getKitStatusOptions = (t: TFunction<'kit-filters'>) => [
	{
		label: t('kit-filters:status.options.available'),
		value: KitStatusFilter.AVAILABLE,
	},
	{
		label: t('kit-filters:status.options.checkedout'),
		value: KitStatusFilter.CHECKEDOUT,
	},
	{
		label: t('kit-filters:status.options.await_checkout'),
		value: KitStatusFilter.AWAIT_CHECKOUT,
	},
	{
		label: t('kit-filters:status.options.in_custody'),
		value: KitStatusFilter.IN_CUSTODY,
	},
	{
		label: t('kit-filters:status.options.empty'),
		value: KitStatusFilter.EMPTY,
	},
	{
		label: t('kit-filters:status.options.incomplete'),
		value: KitStatusFilter.INCOMPLETE,
	},
	{
		label: t('kit-filters:status.options.retired'),
		value: KitStatusFilter.RETIRED,
	},
];

const getLockedOptions = (t: TFunction<'kit-filters'>) => [
	{
		label: t('kit-filters:locked.options.any'),
		value: 'any',
	},
	{
		label: t('kit-filters:locked.options.locked'),
		value: 'locked',
	},
	{
		label: t('kit-filters:locked.options.unlocked'),
		value: 'unlocked',
	},
];

interface UseKitFiltersProps {
	t: TFunction<'kit-filters'>;
	customFields?: CustomField[];
	useLockedKits?: boolean;
}

export const useKitFilters = ({ t, customFields, useLockedKits = false }: UseKitFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const staticFilters: FilterFieldEntry[] = [
			{
				id: 'status',
				name: t('filter.status.trigger'),
				type: 'select',
				options: getKitStatusOptions(t),
			},
			...(useLockedKits
				? [
						{
							id: 'locked',
							name: t('kit-filters:locked.trigger'),
							type: 'select' as const,
							selectMode: 'single' as const,
							allValue: 'any',
							options: getLockedOptions(t),
						},
					]
				: []),
		];

		const dynamicFilters = getDynamicFilterEntries([
			...KIT_SYSTEM_FIELDS,
			...(customFields ?? []).map((field) => ({ ...field, id: `fields.${field.name}` })),
		]);

		return [...staticFilters, ...dynamicFilters];
	}, [t, useLockedKits, customFields]);

	return {
		filters,
	};
};
